import { useEffect, useMemo, useState } from 'react';
import { Avatar, RoleBadge } from '../components/parts';
import { CoinIcon } from '../components/Icon';
import type { GameSession } from '../game/useGameSession';
import type { PlayerStats, Role, Winner } from '../game/types';
import { endingArt } from '../game/art';
import { colorForId } from '../game/cosmetics';
import { fetchWallet } from '../game/shop';
import { track } from '../analytics';
import { useT } from '../i18n';

const WINNER_COLOR = {
  directorSide: 'var(--amber)',
  rebels: 'var(--magenta)',
  fixer: 'var(--lime)',
} as const;

const EMPTY: PlayerStats = { dealt: 0, taken: 0, spikes: 0, blocks: 0, heals: 0 };

const CONFETTI_COLORS = ['#00e5ff', '#ff2e88', '#8a5cff', '#c6ff4d', '#ffb84d'];

/** Lightweight CSS confetti — ~40 pieces with randomized fall paths. */
function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2.2,
        duration: 2.6 + Math.random() * 2,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]!,
        size: 6 + Math.random() * 6,
        spin: Math.random() < 0.5 ? 1 : -1,
      })),
    [],
  );
  return (
    <div className="confetti" aria-hidden>
      {pieces.map((p) => (
        <span
          key={p.id}
          className="confetti-piece"
          style={{
            left: p.left + '%',
            width: p.size,
            height: p.size * 0.45,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            ['--spin' as string]: `${p.spin * 720}deg`,
          }}
        />
      ))}
    </div>
  );
}

/** Which faction banner a role fights under — presentation only. */
function sideOf(role: Role): Winner {
  if (role === 'director' || role === 'security') return 'directorSide';
  return role === 'rebel' ? 'rebels' : 'fixer';
}

export function GameOver({ session }: { session: GameSession }) {
  const t = useT();
  const { game, players, isHost, busy } = session;
  const [copied, setCopied] = useState(false);
  const winner = game?.winner ?? null;
  const color = winner ? WINNER_COLOR[winner] : 'var(--cyan)';
  const background = endingArt(winner);

  // The exact payout for THIS game, written by the server into the caller's
  // wallet in the same transaction that finished the game (matched by seed, so
  // a reconnect into an old finished room never shows a stale amount).
  const [coinsEarned, setCoinsEarned] = useState<number | null>(null);
  const myUid = session.myUid;
  const seed = session.room?.seed;
  useEffect(() => {
    if (!winner || !myUid || !seed || session.spectating) return;
    let alive = true;
    fetchWallet(myUid)
      .then((w) => {
        if (alive && w.lastGame && w.lastGame.gameId === seed) setCoinsEarned(w.lastGame.coins);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [winner, myUid, seed, session.spectating]);

  const stats = game?.stats ?? {};
  const hasStats = players.some((p) => {
    const s = stats[p.uid];
    return s && (s.dealt || s.taken || s.spikes || s.blocks || s.heals);
  });
  // MVP: most damage dealt (blocks break ties).
  const mvpUid = hasStats
    ? [...players].sort((a, b) => {
        const sa = stats[a.uid] ?? EMPTY;
        const sb = stats[b.uid] ?? EMPTY;
        return sb.dealt - sa.dealt || sb.blocks - sa.blocks;
      })[0]?.uid
    : undefined;

  // Share my personal outcome. Web Share API where available (mobile), else
  // copy to clipboard — either way the text carries the game's landing URL.
  const myRole = session.myRole ?? session.me?.revealedRole ?? null;
  const iWon = !!(winner && myRole && sideOf(myRole) === winner);

  function shareResult() {
    if (!myRole) return;
    const text =
      (iWon ? t.gameover.shareWin : t.gameover.shareLoss).replace('{role}', t.role.name[myRole]) +
      (session.myUid === mvpUid ? ' ' + t.gameover.shareMvp : '');
    const url = 'https://neon-fall.com';
    track('share_result', { won: iWon });
    if (navigator.share) {
      navigator.share({ title: 'NEONFALL', text, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(`${text} ${url}`).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div
      className="screen center gameover-screen"
      style={background ? { ['--gameover-bg' as string]: `url(${background})` } : undefined}
    >
      {winner && <Confetti />}
      <div className="subtitle">{t.gameover.title}</div>
      {winner && (
        <div
          className="reveal-card"
          style={{ fontSize: 34, fontWeight: 900, letterSpacing: '0.08em', color }}
        >
          {t.winner[winner]}
        </div>
      )}
      {coinsEarned !== null && (
        <div className="coins-earned"><CoinIcon size={15} /> {t.gameover.coins(coinsEarned)}</div>
      )}

      <div className="col gameover-roster" style={{ gap: 8, width: '100%', marginTop: 12 }}>
        <div className="eyebrow">{t.gameover.roster}</div>
        <div className="seat-list">
        {players.map((p) => {
          const s = stats[p.uid] ?? EMPTY;
          return (
            <div key={p.uid} className={'seat' + (p.alive ? '' : ' dead')}>
              <Avatar player={p} />
              <div className="col" style={{ gap: 3, flex: 1, minWidth: 0 }}>
                <div className="row" style={{ gap: 6 }}>
                  <span style={{ fontWeight: 700, color: colorForId(p.nameColor) }}>{p.displayName}</span>
                  {p.uid === mvpUid && (
                    <span className="tag" style={{ color: 'var(--amber)' }}>★ {t.stats.mvp}</span>
                  )}
                </div>
                {hasStats && (
                  <div className="stat-row">
                    <span title={t.stats.dealt}>⚔ {s.dealt}</span>
                    <span title={t.stats.taken}>♥ {s.taken}</span>
                    <span title={t.stats.spikes}>⚡ {s.spikes}</span>
                    <span title={t.stats.blocks}>🛡 {s.blocks}</span>
                    <span title={t.stats.heals}>✚ {s.heals}</span>
                  </div>
                )}
              </div>
              {p.revealedRole && <RoleBadge role={p.revealedRole} />}
            </div>
          );
        })}
        </div>
        {hasStats && (
          <div className="mono-dim stat-legend">
            ⚔ {t.stats.dealt} · ♥ {t.stats.taken} · ⚡ {t.stats.spikes} · 🛡 {t.stats.blocks} · ✚ {t.stats.heals}
          </div>
        )}
      </div>

      <div className="spacer" />

      <div className="col gameover-actions" style={{ gap: 10, width: '100%' }}>
        {isHost ? (
          <button className="btn block magenta" disabled={busy} onClick={session.rematch}>
            {t.gameover.rematch}
          </button>
        ) : (
          <div className="mono-dim" style={{ textAlign: 'center' }}>{t.gameover.waitHost}</div>
        )}
        {winner && myRole && (
          <button className="btn block cyan" onClick={shareResult}>
            {copied ? t.gameover.shareCopied : t.gameover.share}
          </button>
        )}
        <button className="btn block ghost" onClick={session.leaveToHome}>
          {t.gameover.exit}
        </button>
      </div>
    </div>
  );
}
